import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Users, DollarSign, CircleCheckBig, Clock, Wallet, AlertTriangle, TrendingUp,
} from 'lucide-react';
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend,
} from 'recharts';
import { StatsCard } from '@/components/Dashboard/StatsCard';
import {
    cashflowChartData,
    dashboardSummary,
    overdueData,
    packageComparisonData,
    profitChartData,
    revenueChartData,
} from '@/api/Admin/dashboard';

interface PackageCompareRow {
    name: string;
    value: number;
    packages: number;
    percent: number;
    color: string;
}

interface OverdueRow {
    id: number;
    name: string;
    code: string;
    package: string;
    amount: number;
    days: number;
    level: 'danger' | 'warning';
}

interface SummaryState {
    total_investors: { value: number; trend_percentage: number };
    total_capital_raised: { value: number; trend_percentage: number };
    total_interest_paid: { value: number; trend_percentage: number };
    commission_paid: { value: number; trend_percentage: number };
    pending_commission: { value: number };
    overdue_money: { value: number; overdue_count: number };
    interest_payable_current_period: { value: number };
    profit_margin: { value: number };
}

const PACKAGE_COLORS = ['#F97316', '#A855F7', '#3B82F6', '#10B981', '#EF4444'];

const toNumber = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatCompactAmount = (value: number) => {
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)} tỷ`;
    if (absValue >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} tr`;
    if (absValue >= 1_000) return `${(value / 1_000).toFixed(1)} nghìn`;
    return `${Math.round(value)}`;
};

const formatVND = (v: number) =>
    new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(v);

const chartAxisTick = (v: number) => (v === 0 ? '0 đ' : formatCompactAmount(v));

const toDateInputValue = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function Dashboard() {
    const overduePerPage = 5;
    const [fromDate, setFromDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return toDateInputValue(date);
    });
    const [toDate, setToDate] = useState(() => toDateInputValue(new Date()));

    const [summaryData, setSummaryData] = useState<SummaryState | null>(null);
    const [revenueByMonth, setRevenueByMonth] = useState<{ month: string; value: number }[]>([]);
    const [cashInTotal, setCashInTotal] = useState(0);
    const [newInvestors, setNewInvestors] = useState(0);
    const [interestByMonth, setInterestByMonth] = useState<{ month: string; due: number; paid: number }[]>([]);
    const [profitSummary, setProfitSummary] = useState<{ paid_on_time: number; paid_late: number }>({ paid_on_time: 0, paid_late: 0 });
    const [revenueCostProfit, setRevenueCostProfit] = useState<{ month: string; revenue: number; commission: number; interest: number }[]>([]);
    const [packageComparison, setPackageComparison] = useState<PackageCompareRow[]>([
        { name: 'TITAN', value: 0, packages: 0, percent: 0, color: PACKAGE_COLORS[0] },
        { name: 'PIONEER', value: 0, packages: 0, percent: 0, color: PACKAGE_COLORS[1] },
        { name: 'TRADER', value: 0, packages: 0, percent: 0, color: PACKAGE_COLORS[2] },
    ]);
    const [overduePayments, setOverduePayments] = useState<OverdueRow[]>([]);
    const [overduePage, setOverduePage] = useState(1);

    useEffect(() => {
        let isMounted = true;

        const loadDashboard = async () => {
            try {
                const summaryRes = await dashboardSummary(fromDate, toDate);
                if (!isMounted) return;
                setSummaryData({
                    total_investors: {
                        value: toNumber(summaryRes.data.total_investors?.value),
                        trend_percentage: toNumber(summaryRes.data.total_investors?.trend_percentage),
                    },
                    total_capital_raised: {
                        value: toNumber(summaryRes.data.total_capital_raised?.value),
                        trend_percentage: toNumber(summaryRes.data.total_capital_raised?.trend_percentage),
                    },
                    total_interest_paid: {
                        value: toNumber(summaryRes.data.total_interest_paid?.value),
                        trend_percentage: toNumber(summaryRes.data.total_interest_paid?.trend_percentage),
                    },
                    commission_paid: {
                        value: toNumber(summaryRes.data.commission_paid?.value),
                        trend_percentage: toNumber(summaryRes.data.commission_paid?.trend_percentage),
                    },
                    pending_commission: {
                        value: toNumber(summaryRes.data.pending_commission?.value),
                    },
                    overdue_money: {
                        value: toNumber(summaryRes.data.overdue_money?.value),
                        overdue_count: toNumber(summaryRes.data.overdue_money?.overdue_count),
                    },
                    interest_payable_current_period: {
                        value: toNumber(summaryRes.data.interest_payable_current_period?.value),
                    },
                    profit_margin: {
                        value: toNumber(summaryRes.data.profit_margin?.value),
                    },
                });
            } catch {
                if (!isMounted) return;
                setSummaryData(null);
            }

            try {
                const packageRes = await packageComparisonData(fromDate, toDate);
                if (!isMounted) return;
                setPackageComparison(
                    (packageRes.data.packages || []).map((item, index) => ({
                        name: item.name,
                        value: toNumber(item.total_amount),
                        packages: toNumber(item.count),
                        percent: toNumber(item.percentage),
                        color: PACKAGE_COLORS[index % PACKAGE_COLORS.length],
                    }))
                );
            } catch {
                if (!isMounted) return;
                setPackageComparison([]);
            }

            try {
                const profitRes = await profitChartData(fromDate, toDate);
                if (!isMounted) return;
                setProfitSummary({
                    paid_on_time: toNumber(profitRes.data.summary.paid_on_time),
                    paid_late: toNumber(profitRes.data.summary.paid_late),
                });
                setInterestByMonth(
                    (profitRes.data.chart_data || []).map(item => ({
                        month: item.label,
                        due: toNumber(item.payable),
                        paid: toNumber(item.paid),
                    }))
                );
            } catch {
                if (!isMounted) return;
                setInterestByMonth([]);
                setProfitSummary({ paid_on_time: 0, paid_late: 0 });
            }

            try {
                const revenueRes = await revenueChartData(fromDate, toDate);
                if (!isMounted) return;
                setRevenueCostProfit(
                    (revenueRes.data.chart_data || []).map(item => ({
                        month: item.label,
                        revenue: toNumber(item.revenue),
                        commission: toNumber(item.commission_cost),
                        interest: toNumber(item.interest_cost),
                    }))
                );
            } catch {
                if (!isMounted) return;
                setRevenueCostProfit([]);
            }

            try {
                const overdueRes = await overdueData(fromDate, toDate, 1, 12);
                if (!isMounted) return;
                setOverduePayments(
                    (overdueRes.data || []).map(item => ({
                        id: item.id,
                        name: item.user_info?.fullname || `User #${item.user_id}`,
                        code: item.user_info?.email || `USER-${item.user_id}`,
                        package: item.package,
                        amount: toNumber(item.amount),
                        days: toNumber(item.days_late),
                        level: item.severity_level === 'danger' ? 'danger' : 'warning',
                    }))
                );
            } catch {
                if (!isMounted) return;
                setOverduePayments([]);
            }

            try {
                const cashRes = await cashflowChartData(fromDate, toDate);
                if (!isMounted) return;
                setCashInTotal(toNumber(cashRes.data.total_cash_in.value));
                setNewInvestors(toNumber(cashRes.data.new_investors.value));
                setRevenueByMonth(
                    (cashRes.data.chart_data || []).map(item => ({
                        month: item.label,
                        value: toNumber(item.value),
                    }))
                );
            } catch {
                if (!isMounted) return;
                setRevenueByMonth([]);
                setCashInTotal(0);
                setNewInvestors(0);
            }
        };

        loadDashboard();
        return () => {
            isMounted = false;
        };
    }, [fromDate, toDate]);


    const topPackage = useMemo(() => {
        if (packageComparison.length === 0) return null;
        return [...packageComparison].sort((a, b) => b.value - a.value)[0];
    }, [packageComparison]);

    const overdueTotalPages = useMemo(() => {
        return Math.max(1, Math.ceil(overduePayments.length / overduePerPage));
    }, [overduePayments.length]);

    const paginatedOverduePayments = useMemo(() => {
        const startIndex = (overduePage - 1) * overduePerPage;
        return overduePayments.slice(startIndex, startIndex + overduePerPage);
    }, [overduePage, overduePayments]);

    useEffect(() => {
        if (overduePage > overdueTotalPages) {
            setOverduePage(overdueTotalPages);
        }
    }, [overduePage, overdueTotalPages]);

    useEffect(() => {
        setOverduePage(1);
    }, [fromDate, toDate]);

    return (
        <div className="min-h-screen font-sans">
            <Helmet><title>Dashboard</title></Helmet>

            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tổng quan</h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Tổng quan hệ thống BEQ Rewards</p>
                </div>
                <div className="self-start md:self-auto flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
                    <input
                        type="date"
                        value={fromDate}
                        max={toDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none"
                    />
                    <span className="text-gray-400">-</span>
                    <input
                        type="date"
                        value={toDate}
                        min={fromDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <StatsCard 
                    title="Tổng nhà đầu tư" 
                    value={String(summaryData?.total_investors?.value ?? 0)} 
                    change={`${(summaryData?.total_investors?.trend_percentage ?? 0) > 0 ? '+' : ''}${(summaryData?.total_investors?.trend_percentage ?? 0).toFixed(1)}%`} 
                    changeLabel="so với tháng trước"
                    trend={(summaryData?.total_investors?.trend_percentage ?? 0) >= 0 ? 'up' : 'neutral'}
                    icon={Users} 
                    bgColor="bg-blue-50 dark:bg-blue-900/20" 
                    iconColor="text-blue-600 dark:text-blue-400"
                    chartData={[]}
                    chartColor="#3B82F6"
                    gradientColor="#3B82F6"
                    index={0}
                />
                <StatsCard 
                    title="Tổng vốn huy động" 
                    value={formatCompactAmount(summaryData?.total_capital_raised?.value ?? 0)} 
                    change={`${(summaryData?.total_capital_raised?.trend_percentage ?? 0) > 0 ? '+' : ''}${(summaryData?.total_capital_raised?.trend_percentage ?? 0).toFixed(1)}%`} 
                    changeLabel="so với tháng trước"
                    trend={(summaryData?.total_capital_raised?.trend_percentage ?? 0) >= 0 ? 'up' : 'neutral'}
                    icon={DollarSign} 
                    bgColor="bg-purple-50 dark:bg-purple-900/20" 
                    iconColor="text-purple-600 dark:text-purple-400"
                    chartData={[]}
                    chartColor="#A855F7"
                    gradientColor="#A855F7"
                    index={1}
                />
                <StatsCard 
                    title="Tổng lãi đã trả" 
                    value={formatCompactAmount(summaryData?.total_interest_paid?.value ?? 0)} 
                    change={`${(summaryData?.total_interest_paid?.trend_percentage ?? 0) > 0 ? '+' : ''}${(summaryData?.total_interest_paid?.trend_percentage ?? 0).toFixed(1)}%`} 
                    changeLabel="so với tháng trước"
                    trend={(summaryData?.total_interest_paid?.trend_percentage ?? 0) >= 0 ? 'up' : 'neutral'}
                    icon={CircleCheckBig} 
                    bgColor="bg-emerald-50 dark:bg-emerald-900/20" 
                    iconColor="text-emerald-600 dark:text-emerald-400"
                    chartData={[]}
                    chartColor="#10B981"
                    gradientColor="#10B981"
                    index={2}
                />
                <StatsCard 
                    title="Lãi phải trả (kỳ này)" 
                    value={formatCompactAmount(summaryData?.interest_payable_current_period?.value ?? 0)} 
                    change=""
                    changeLabel="so với tháng trước"
                    trend="neutral"
                    icon={Clock} 
                    bgColor="bg-orange-50 dark:bg-orange-900/20" 
                    iconColor="text-orange-600 dark:text-orange-400"
                    chartData={[]}
                    chartColor="#F97316"
                    gradientColor="#F97316"
                    index={3}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatsCard 
                    title="Hoa hồng đã trả" 
                    value={formatCompactAmount(summaryData?.commission_paid?.value ?? 0)} 
                    change={`${(summaryData?.commission_paid?.trend_percentage ?? 0) > 0 ? '+' : ''}${(summaryData?.commission_paid?.trend_percentage ?? 0).toFixed(1)}%`} 
                    changeLabel="so với tháng trước"
                    trend={(summaryData?.commission_paid?.trend_percentage ?? 0) >= 0 ? 'up' : 'neutral'}
                    icon={Wallet} 
                    bgColor="bg-blue-50 dark:bg-blue-900/20" 
                    iconColor="text-blue-600 dark:text-blue-400"
                    chartData={[]}
                    chartColor="#3B82F6"
                    gradientColor="#3B82F6"
                    index={4}
                />
                <StatsCard 
                    title="Tiền quá hạn" 
                    value={formatCompactAmount(summaryData?.overdue_money?.value ?? 0)} 
                    change={`${summaryData?.overdue_money?.overdue_count ?? 0} khoản`} 
                    changeLabel=""
                    trend="neutral"
                    icon={AlertTriangle} 
                    bgColor="bg-red-50 dark:bg-red-900/20" 
                    iconColor="text-red-600 dark:text-red-400"
                    chartData={[]}
                    chartColor="#EF4444"
                    gradientColor="#EF4444"
                    index={5}
                />
                <StatsCard 
                    title="HH chờ xử lý" 
                    value={formatCompactAmount(summaryData?.pending_commission?.value ?? 0)} 
                    change="Chưa thanh toán" 
                    changeLabel=""
                    trend="neutral"
                    icon={Clock} 
                    bgColor="bg-gray-100 dark:bg-gray-700" 
                    iconColor="text-gray-500 dark:text-gray-400"
                    chartData={[]}
                    chartColor="#6B7280"
                    gradientColor="#6B7280"
                    index={6}
                />
                <StatsCard 
                    title="Biên lợi nhuận" 
                    value={`${summaryData?.profit_margin?.value ?? 0}%`} 
                    change="Tháng này" 
                    changeLabel="Tốt"
                    trend="up"
                    icon={TrendingUp} 
                    bgColor="bg-emerald-50 dark:bg-emerald-900/20" 
                    iconColor="text-emerald-600 dark:text-emerald-400"
                    chartData={[]}
                    chartColor="#10B981"
                    gradientColor="#10B981"
                    index={7}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Revenue Area Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white">Tiền vào theo tháng</h3>
                    </div>
                    <div className="flex gap-8 mb-4">
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Tổng tiền vào</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCompactAmount(cashInTotal)}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">NĐT mới (tháng này)</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{newInvestors}</p>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={revenueByMonth}>
                            <defs>
                                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                            <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={chartAxisTick} />
                            <Tooltip formatter={(v: number) => [formatVND(v), 'Tiền vào']} />
                            <Area type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2.5} fill="url(#areaGrad)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                {/* Package Comparison */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">So sánh gói đầu tư</h3>
                    {/* Package summary cards */}
                    <div className="grid grid-cols-3 gap-3 mb-6">
                        {[...packageComparison].reverse().map(pkg => (
                            <div key={pkg.name} className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pkg.color }} />
                                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{pkg.name}</span>
                                </div>
                                <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCompactAmount(pkg.value)}</p>
                                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                    <span>{pkg.packages} gói</span>
                                    <span>{pkg.percent}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={packageComparison} layout="vertical" barSize={28}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                            <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={chartAxisTick} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fontWeight: 600 }} stroke="#9ca3af" width={70} />
                            <Tooltip formatter={(v: number) => [formatVND(v), 'Đầu tư']} />
                            <Bar dataKey="value" radius={[0, 6, 6, 0]}
                                fill="#3B82F6"
                                // Color each bar individually
                                shape={(props: any) => {
                                    const pkg = packageComparison.find(p => p.name === props.name);
                                    return <rect {...props} fill={pkg?.color || '#3B82F6'} rx={6} />;
                                }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                    {/* Insight */}
                    {topPackage ? (
                        <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 flex items-center gap-3">
                            <span className="text-xl">🏆</span>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>{topPackage.name}</strong> là gói được đầu tư nhiều nhất với <strong>{formatCompactAmount(topPackage.value)}</strong> ({topPackage.percent}%)
                            </p>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Interest Payment Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">Lãi trả theo tháng</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={interestByMonth} barGap={4}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                            <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={chartAxisTick} />
                            <Tooltip 
                                formatter={(v: number) => formatVND(v)}
                                contentStyle={{ 
                                    backgroundColor: 'rgba(255, 255, 255, 0.96)', 
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    fontSize: '12px'
                                }}
                            />
                            <Legend iconType="circle" iconSize={8}
                                formatter={(val) => <span className="text-xs text-gray-600 dark:text-gray-400">{val}</span>} />
                            <Bar dataKey="due" name="Phải trả" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={20} />
                            <Bar dataKey="paid" name="Đã trả" fill="#10B981" radius={[4, 4, 0, 0]} barSize={20} />
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Đã trả đúng hạn</p>
                            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatCompactAmount(profitSummary.paid_on_time)}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Trả trễ</p>
                            <p className="text-lg font-bold text-red-600 dark:text-red-400">{formatCompactAmount(profitSummary.paid_late)}</p>
                        </div>
                    </div>
                </div>

                {/* Revenue/Cost/Profit Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">Doanh thu - Hoa hồng - Trả lãi</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={revenueCostProfit} barGap={2}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                            <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={chartAxisTick} />
                            <Tooltip 
                                formatter={(v: number) => formatVND(v)}
                                contentStyle={{ 
                                    backgroundColor: 'rgba(255, 255, 255, 0.96)', 
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    fontSize: '12px'
                                }} 
                            />
                            <Legend iconType="circle" iconSize={8}
                                formatter={(val) => <span className="text-xs text-gray-600 dark:text-gray-400">{val}</span>} />
                            <Bar dataKey="commission" name="Chi HH" fill="#F97316" radius={[4, 4, 0, 0]} barSize={16} />
                            <Bar dataKey="interest" name="Chi lãi" fill="#FBBF24" radius={[4, 4, 0, 0]} barSize={16} />
                            <Bar dataKey="revenue" name="Doanh thu" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={16} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">Chi tiết khoản quá hạn</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Top 12 khoản trả lãi quá hạn</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                                {['STT', 'NHÀ ĐẦU TƯ', 'MÃ GÓI', 'SỐ TIỀN', 'SỐ NGÀY TRỄ', 'MỨC ĐỘ'].map(h => (
                                    <th key={h} className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {paginatedOverduePayments.map((row, index) => (
                                <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{(overduePage - 1) * overduePerPage + index + 1}</td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">{row.name}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{row.code}</div>
                                    </td>
                                    <td className="px-6 py-4 text-sm font-medium text-blue-600 dark:text-blue-400">{row.package}</td>
                                    <td className="px-6 py-4 text-sm font-medium text-red-600 dark:text-red-400">{formatVND(row.amount)}</td>
                                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-white">{row.days} ngày</td>
                                    <td className="px-6 py-4">
                                        {row.level === 'danger' ? (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                                <span className="w-2 h-2 rounded-full bg-red-500" /> Nguy hiểm
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                                <span className="w-2 h-2 rounded-full bg-amber-500" /> Cảnh báo
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900/20 rounded-b-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        Hiển thị <span className="font-medium">{overduePayments.length > 0 ? (overduePage - 1) * overduePerPage + 1 : 0}</span>–
                        <span className="font-medium">{Math.min(overduePage * overduePerPage, overduePayments.length)}</span> trong <span className="font-medium">{overduePayments.length}</span> kết quả
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            disabled={overduePage === 1}
                            onClick={() => setOverduePage(prev => Math.max(1, prev - 1))}
                            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Trước
                        </button>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            {overduePage}/{overdueTotalPages}
                        </span>
                        <button
                            type="button"
                            disabled={overduePage >= overdueTotalPages}
                            onClick={() => setOverduePage(prev => Math.min(overdueTotalPages, prev + 1))}
                            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Sau
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

